import React, { useContext, useEffect, useRef, useState } from "react";
import cx from "classnames";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import global from "../../scss/dhp.scss";
import { Icon } from "./icon";
import { AppContext } from "../../contexts";
import { generateUniqueHash } from "../../_helpers/utils";
import Progressbar from "./progressbar";

let style = Object.assign({}, global);

const AssetLoader = ({ assetId }) => {
  const [uploadProgress, setUploadProgress] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const intervalRef = useRef(null);

  const calculateProgress = val => {
    let progress = val ?? 0;
    let maxProgress = 95;
    intervalRef.current = setInterval(() => {
      if (progress < maxProgress) {
        progress += 3;
        if (progress > maxProgress) {
          progress = maxProgress;
        }
      }
      setUploadProgress(progress);
      if (progress >= maxProgress) {
        clearInterval(intervalRef.current);
      }
    }, 400);
  };

  useEffect(() => {
    calculateProgress();
  }, []);

  useEffect(() => {
    if (!assetId) setShowLoader(true);
    else if (showLoader) {
      clearInterval(intervalRef.current);
      setUploadProgress(100);
      setTimeout(() => setShowLoader(false), 1000);
    }
  }, [assetId]);

  return (
    <>
      {showLoader && (
        <div className={style["loading"]}>
          <div
            className={cx(
              style["progress"],
              style["download-progressbar"],
              style["my-4"],
              style["m-auto"],
              style["w-75"]
            )}>
            <Progressbar progressbarInfo={uploadProgress} />
          </div>
        </div>
      )}
    </>
  );
};

AssetLoader.propTypes = {
  assetId: PropTypes.string,
};

const AssetField = ({
  type,
  acceptType,
  maxSize,
  defaultAssets = [],
  updateFunction,
  deleteAssetFn,
  currentAssetList = [],
  maxCount = null,
  setCustomMsg,
  emptyAssetList = false,
}) => {
  const brandInfo = useSelector(state => state.company?.brandInfo);
  const { updateFlashMessage } = useContext(AppContext);

  const [assetList, setAssetList] = useState(defaultAssets);
  const [assetTypeArray, setAssetTypeArray] = useState([]);
  const [pendingAssets, setPendingAssets] = useState([]);
  const [deletedAssetId, setDeletedAssetId] = useState();

  // Calculate if user can upload more files
  const canUploadMore = maxCount === null || assetList.length < maxCount;

  const fileValidationCheck = async file => {
    let fleSize = file.size / 1e6;
    let fileFormat = file.type.split("/");
    let allowedTypes = assetTypeArray;
    let allowedImageTypes = ["jpeg", "jpg", "png", "webp"];
    let allowedSvgTypes = ["svg+xml"];
    let allowedGifTypes = ["gif"];
    let allowedVideoTypes = ["mp4", "mov", "webm", "quicktime"];
    if (
      file &&
      allowedTypes.includes(fileFormat[1].toLowerCase()) &&
      ((allowedImageTypes.includes(fileFormat[1].toLowerCase()) && fleSize <= maxSize.image) ||
        (allowedSvgTypes.includes(fileFormat[1].toLowerCase()) && fleSize <= maxSize.svg) ||
        (allowedGifTypes.includes(fileFormat[1].toLowerCase()) && fleSize <= maxSize.gif) ||
        (allowedVideoTypes.includes(fileFormat[1].toLowerCase()) && fleSize <= maxSize.video))
    ) {
      return true;
    } else if (
      (allowedImageTypes.includes(fileFormat[1].toLowerCase()) && fleSize > maxSize.image) ||
      (allowedSvgTypes.includes(fileFormat[1].toLowerCase()) && fleSize > maxSize.svg) ||
      (allowedGifTypes.includes(fileFormat[1].toLowerCase()) && fleSize > maxSize.gif) ||
      (allowedVideoTypes.includes(fileFormat[1].toLowerCase()) && fleSize > maxSize.video)
    ) {
      let ErrorBody = {
        type: "error",
        message: `File Size should be maximum ${
          allowedImageTypes.includes(fileFormat[1].toLowerCase())
            ? maxSize.image
            : allowedSvgTypes.includes(fileFormat[1].toLowerCase())
            ? maxSize.svg
            : allowedGifTypes.includes(fileFormat[1].toLowerCase())
            ? maxSize.gif
            : maxSize.video
        } MB.`,
      };
      if (setCustomMsg) {
        setCustomMsg(ErrorBody);
      } else updateFlashMessage(ErrorBody);
      return false;
    } else {
      if (setCustomMsg) {
        setCustomMsg({
          type: "error",
          message: "Invalid Format!!",
        });
      } else
        updateFlashMessage({
          type: "error",
          message: "Invalid Format!!",
        });

      return false;
    }
  };

  const uploadBrandAsset = (files, hashkey) => {
    setPendingAssets([...pendingAssets, hashkey]);
    if (updateFunction) updateFunction(files, hashkey);
  };

  const uploadImages = e => {
    if (!canUploadMore) {
      if (setCustomMsg) {
        setCustomMsg({
          type: "error",
          message: `Maximum ${maxCount} images allowed.`,
        });
      } else {
        updateFlashMessage({
          type: "error",
          message: `Maximum ${maxCount} images allowed.`,
        });
      }

      return;
    }

    const files = Array.from(e.target.files);

    // Check if adding these files would exceed maxCount
    if (maxCount !== null && assetList.length + files.length > maxCount) {
      if (setCustomMsg) {
        setCustomMsg({
          type: "error",
          message: `You can only upload ${maxCount - assetList.length} more image(s).`,
        });
      } else {
        updateFlashMessage({
          type: "error",
          message: `You can only upload ${maxCount - assetList.length} more image(s).`,
        });
      }

      e.target.value = "";
      return;
    }

    let hashkey = `asset${generateUniqueHash()}`;
    // const files = Array.from(e.target.files);
    let fileDataArray = [];

    let validFile = 0;
    files.map(async file => {
      let gotoNext = await fileValidationCheck(file);
      if (!gotoNext) return;

      validFile += 1;
      const reader = new FileReader();
      reader.onload = e => {
        fileDataArray.push({ url: e.target.result, temp_id: hashkey });
        if (fileDataArray.length === files.length) {
          setAssetList([...assetList, ...fileDataArray]);
        }
      };
      reader.readAsDataURL(file);

      // Upload file if all validation check
      if (validFile === files.length) uploadBrandAsset(files, hashkey);
    });

    e.target.value = ""; // cleaning the file upload field
  };

  const uploadVideos = e => {
    if (!canUploadMore) {
      if (setCustomMsg) {
        setCustomMsg({
          type: "error",
          message: `Maximum ${maxCount} videos allowed.`,
        });
      } else {
        updateFlashMessage({
          type: "error",
          message: `Maximum ${maxCount} videos allowed.`,
        });
      }

      return;
    }

    // For videos, typically you'd want single selection
    if (maxCount !== null && assetList.length >= maxCount) {
      if (setCustomMsg) {
        setCustomMsg({
          type: "error",
          message: `Maximum ${maxCount} videos allowed.`,
        });
      } else {
        updateFlashMessage({
          type: "error",
          message: `Maximum ${maxCount} videos allowed.`,
        });
      }

      e.target.value = "";
      return;
    }

    let hashkey = `asset${generateUniqueHash()}`;
    const files = Array.from(e.target.files);
    let fileDataArray = [];
    let videoData = {};

    let validFile = 0;
    files.map(async file => {
      let gotoNext = await fileValidationCheck(file);
      if (!gotoNext) return;

      validFile += 1;
      videoData["videoFile"] = file;
      const videoURL = URL.createObjectURL(file);

      // Create a video element
      const videoPlayer = document.createElement("video");
      videoPlayer.src = videoURL;
      videoPlayer.preload = "metadata";

      // Capture a frame at a specific time (e.g., 5 seconds)
      videoPlayer.addEventListener("seeked", async function () {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = videoPlayer.videoWidth;
          canvas.height = videoPlayer.videoHeight;
          canvas.getContext("2d").drawImage(videoPlayer, 0, 0, canvas.width, canvas.height);
          const thumbnailDataURL = canvas.toDataURL("image/png"); // You can choose the image format here

          // Append the thumbnail to the container
          fileDataArray.push({ thumb: thumbnailDataURL, temp_id: hashkey });
          setAssetList([...assetList, ...fileDataArray]);
        } catch (error) {
          console.error(error);
        }

        // Upload file if all validation check
        if (validFile === files.length) uploadBrandAsset(videoData, hashkey);

        // Clean up
        URL.revokeObjectURL(videoURL);
      });

      // Set the video's currentTime after metadata has loaded
      videoPlayer.addEventListener("loadedmetadata", function () {
        const halfwayTime = videoPlayer.duration / 2;
        videoPlayer.currentTime = halfwayTime; // Set the desired time in seconds
      });
    });

    e.target.value = ""; // cleaning the file upload field
  };

  const fileSelectedHandler = e => {
    if (!canUploadMore) {
      if (setCustomMsg) {
        setCustomMsg({
          type: "error",
          message: `Maximum ${maxCount} ${type}s allowed.`,
        });
      } else {
        updateFlashMessage({
          type: "error",
          message: `Maximum ${maxCount} ${type}s allowed.`,
        });
      }

      e.target.value = "";
      return;
    }

    if (type === "image") uploadImages(e);
    else if (type === "video") uploadVideos(e);
  };

  const deleteAsset = assetId => {
    setDeletedAssetId(assetId);
    if (deleteAssetFn) deleteAssetFn(assetId);
  };

  const clearPendingAssets = hashKey => {
    const newPendingAssets = pendingAssets.filter(item => item !== hashKey);
    setPendingAssets([...newPendingAssets]);
  };

  useEffect(() => {
    if (type === "image") {
      let newType = acceptType
        ?.replace(/image\//g, "")
        .replace(/\s+/g, "")
        .split(",");
      setAssetTypeArray(newType);
    } else if (type === "video") {
      let newType = acceptType
        ?.replace(/video\//g, "")
        .replace(/\s+/g, "")
        .split(",");
      setAssetTypeArray(newType);
    }

    return () => {
      if (setCustomMsg) {
        setCustomMsg(null);
      } else updateFlashMessage(null);
    };
  }, []);

  useEffect(() => {
    pendingAssets.forEach(hashKey => {
      if (brandInfo[hashKey]) {
        let newAssetList = [];
        assetList.forEach(item => {
          if (item.id) newAssetList.push(item);
        });

        brandInfo[hashKey]?.forEach(asset => {
          let newObj = { id: asset.id, url: asset.url };
          if (asset?.thumb) newObj["thumb"] = asset.thumb;
          if (asset?.duration) newObj["duration"] = asset.duration;
          newAssetList.push(newObj);
        });

        // Assign with new filtered array
        setAssetList([...newAssetList]);

        // clearPendingAssets
        clearPendingAssets(hashKey);
      }
    });

    // Remove deleted asset
    if (brandInfo?.lastDeletedAsset === deletedAssetId && deletedAssetId !== undefined) {
      const newAssetList = assetList.filter(item => item.id !== deletedAssetId);
      // Assign with new filtered array
      setAssetList([...newAssetList]);
      setDeletedAssetId(undefined);
    }
  }, [brandInfo]);

  useEffect(() => {
    if (currentAssetList.length > 0 && emptyAssetList) {
      setTimeout(() => {
        setAssetList([...currentAssetList]);
      }, 100);
    } else if (currentAssetList.length == 0 && emptyAssetList) {
      setTimeout(() => {
        setAssetList([]);
      }, 100);
    }
  }, [currentAssetList]);

  return (
    <div className={cx(style["asset-palette"], style["mb-4"])}>
      <div className={cx(style["asset-palette-list"], { [style["for-video"]]: type === "video" })}>
        {assetList?.map((asset, index) => (
          <div key={index} className={cx(style["asset-container"], { [style["video-thumb"]]: type === "video" })}>
            {asset?.id && (
              <div className={style["delete-img"]} onClick={() => deleteAsset(asset.id)}>
                <Icon icon="ui-trash" />
              </div>
            )}
            <div className={cx(style["asset-thumb"])}>
              <img src={type === "video" ? asset?.thumb : asset?.url} />
            </div>
            <AssetLoader assetId={asset?.id} />
            {asset.duration && type === "video" && <div className={style["duration"]}>{asset.duration}</div>}
          </div>
        ))}
        {canUploadMore && (
          <div className={cx(style["fileUpload"])}>
            <div className={cx(style["asset-box"], style["add-new"], { [style["video-type"]]: type === "video" })}>
              <Icon icon="ui-plus" />
            </div>
            <input
              type="file"
              multiple={type === "video" ? false : true}
              accept={acceptType}
              onChange={e => fileSelectedHandler(e)}
              title=""
            />
          </div>
        )}
      </div>
    </div>
  );
};

AssetField.propTypes = {
  type: PropTypes.string.isRequired,
  acceptType: PropTypes.string.isRequired,
  maxSize: PropTypes.number.isRequired,
  defaultAssets: PropTypes.array.isRequired,
  updateFunction: PropTypes.func.isRequired,
  deleteAssetFn: PropTypes.func.isRequired,
};

export default AssetField;
